import { type ReactElement } from 'react';
import type { VendorDirectoryEntry } from '../../domain/types';
import { FilterTiles } from '../master/FilterTiles';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from './GridPaginationBar';

interface VendorsGridProps {
  rows: VendorDirectoryEntry[];
  selectedVendorId: string | null;
  onSelectVendor: (id: string) => void;
  loading?: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const VENDOR_COLUMNS: GroupedColumnDef[] = [
  { id: 'name', label: 'Name', header: 'Name' },
  { id: 'email', label: 'Email', header: 'Email' },
  { id: 'address', label: 'Address', header: 'Address' }
];

function getGroupValue(row: VendorDirectoryEntry, columnId: string): string {
  switch (columnId) {
    case 'name':
      return row.name || '—';
    case 'email':
      return row.email?.trim() || '—';
    case 'address':
      return row.address || '—';
    default:
      return '—';
  }
}

export function VendorsGrid({
  rows,
  selectedVendorId,
  onSelectVendor,
  loading,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange
}: VendorsGridProps): ReactElement {
  function renderRow(row: VendorDirectoryEntry): ReactElement {
    return (
      <tr
        className={selectedVendorId === row.id ? 'selected' : undefined}
        onClick={() => onSelectVendor(row.id)}
        onDoubleClick={(e) => {
          e.preventDefault();
          onSelectVendor(row.id);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectVendor(row.id);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <td>{row.name}</td>
        <td>
          {row.email?.trim() ? (
            <a
              className="email-in-grid"
              href={`mailto:${encodeURIComponent(row.email.trim())}`}
              onClick={(e) => e.stopPropagation()}
            >
              {row.email.trim()}
            </a>
          ) : (
            <span className="hint">—</span>
          )}
        </td>
        <td>{row.address}</td>
      </tr>
    );
  }

  return (
    <>
      <FilterTiles
        testId="vendors-tiles"
        value="all"
        onChange={() => undefined}
        options={[{ id: 'all', label: 'All', count: totalCount }]}
      />
      <section className="card requests-grid-card">
        <div className="row spread requests-grid-toolbar">
          <h2>Vendors</h2>
        </div>
        <p className="hint">
          Click a row for the side panel. Email is used for RFQ invitations. Drag the grip (⠿) beside a title into the
          strip to group.
        </p>
        {loading && <p className="hint">Loading vendors…</p>}
        <GroupedDataGrid<VendorDirectoryEntry>
          columns={VENDOR_COLUMNS}
          rows={rows}
          getRowId={(r) => r.id}
          getGroupValue={getGroupValue}
          renderRow={renderRow}
          colSpan={3}
          emptyMessage="No vendors in this view."
        />
        <GridPaginationBar
          idPrefix="vendors-grid"
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </section>
    </>
  );
}
